import Cookies from "js-cookie";
import { API_ENDPOINTS } from "constants/http-endpoints";
import { getAccountId, getProductId, getUser } from "utils/localStorage";
import { extractTokenInfo } from "utils/tokenDecoder";
import { parseEnrichedDomainContext, type ParsedEnrichedDomainContext } from "./brandProfileConstants";
import type { CompetitorEntry } from "./competitorConstants";

export const AEO_AUDIT_REQUEST_ID_KEY = "aeo_audit_request_id";
export const AEO_PENDING_AUDIT_ID_KEY = "aeo_pending_audit_id";
/** Set on signup when `utm_source=aeo_visibility`; consumed on get-started to auto-select Cited By. */
export const AEO_UTM_SOURCE_KEY = "aeo_utm_source";
export const AEO_VISIBILITY_UTM_VALUE = "aeo_visibility";

/** Cited By / AI Visibility product telemetry event names. */
export const AEO_EVENTS = {
    PRODUCT_SELECTED: "aeo_onboarding_product_selected",
    INITIALIZED_PROFILE: "aeo_initialized_profile",
    WORKSPACE_SELECTED: "aeo_onboarding_workspace_selected",
    MARKETING_WEBSITE_ENTERED: "aeo_onboarding_marketing_website_entered",
    DETAILS_COMPLETED: "aeo_onboarding_details_completed",
    AUDIENCE_SELECTED: "aeo_onboarding_audience_selected",
    COMPETITORS_SELECTED: "aeo_onboarding_competitors_selected",
    TOPICS_SELECTED: "aeo_onboarding_topics_selected",
    PROMPTS_SAVED: "aeo_onboarding_prompts_saved",
    ONBOARDING_COMPLETED: "aeo_onboarding_completed",
    DOSSIER_VIEWED: "aeo_dossier_viewed",
    CHECKOUT_INITIATED: "aeo_checkout_initiated",
    CHECKOUT_CANCELLED: "aeo_checkout_cancelled",
    CHECKOUT_COMPLETED: "aeo_checkout_completed",
    AUDIT_SCAN_STARTED: "aeo_audit_scan_started",
    PROBE_SCAN_STARTED: "aeo_probe_scan_started",
    PROMPT_RESEARCH_GENERATE_STARTED: "aeo_prompt_research_generate_started",
    PROMPT_RESEARCH_GENERATE_COMPLETED: "aeo_prompt_research_generate_completed",
    PROMPT_RESEARCH_PROMPTS_TRACKED: "aeo_prompt_research_prompts_tracked",
    GSC_CONNECTED: "aeo_gsc_connected",
    GSC_FAILED: "aeo_gsc_failed",
    AI_CRAWL_CONNECTED: "aeo_ai_crawl_connected",
    AI_CRAWL_FAILED: "aeo_ai_crawl_failed"
} as const;

/**
 * Shared dims for AI Visibility product telemetry.
 * - flow_type: agency vs single-brand (token.agency), same as onboarding events
 * - marketing_website: active audited brand domain
 * - brand_product_id: active Cited By brand product (switches per brand in agency)
 *
 * Lands in ClickHouse default.events under ThriveStack product_id via thriveStack.track;
 * account is context.group_id. Do not put full prompt text in properties.
 */
export function aeoProductTelemetryBase(marketingWebsite = ""): Record<string, unknown> {
    const token = Cookies.get("system_token") || "";
    const info = extractTokenInfo(token);
    const domain =
        marketingWebsite.trim().toLowerCase().replace(/^www\./, "") ||
        (localStorage.getItem("activeBrandDomain") || localStorage.getItem("aeo_domain") || "")
            .trim()
            .toLowerCase()
            .replace(/^www\./, "");
    return {
        flow_type: info.agency === true ? "agency" : "yourself",
        marketing_website: domain,
        brand_product_id: getProductId() || info.productId || ""
    };
}

export function trackAeoEvent(eventName: string, properties: Record<string, unknown> = {}): void {
    const ts = (window as { thriveStack?: { track?: (events: unknown[]) => void } }).thriveStack;
    if (!ts || typeof ts.track !== "function") return;
    const user = getUser();
    const accountId = getAccountId() || "";
    ts.track([
        {
            event_name: eventName,
            properties,
            user_id: user?.id || "",
            context: { group_id: accountId, source: "product" },
            timestamp: new Date().toISOString()
        }
    ]);
}

/** One-shot per full page load — GSC connect telemetry must not double-fire on Strict Mode remount. */
let gscConnectResultEmitted = false;

/**
 * Single emitter for Search Console connect outcome. Call only from GscIndexingSection once the
 * result is final (OAuth error, or connected + property selected + domain confirmed).
 */
export function trackGscConnectResult(
    result: "connected" | "failed",
    marketingWebsite: string,
    extra: Record<string, unknown> = {}
): void {
    if (gscConnectResultEmitted) return;
    gscConnectResultEmitted = true;
    trackAeoEvent(result === "connected" ? AEO_EVENTS.GSC_CONNECTED : AEO_EVENTS.GSC_FAILED, {
        ...aeoProductTelemetryBase(marketingWebsite),
        provider: "google",
        ...extra
    });
}

const aiCrawlConnectEmitted = new Set<string>();

/**
 * Single emitter for AI Crawl Attribution setup outcome. Connected is one-shot until
 * resetAiCrawlConnectTelemetry (remove + reconnect). Failed is one-shot per reason so a
 * retryable deploy error can still be followed by a later connected event.
 */
export function trackAiCrawlConnectResult(
    result: "connected" | "failed",
    marketingWebsite: string,
    extra: Record<string, unknown> = {}
): void {
    const key = result === "connected" ? "connected" : `failed:${String(extra.reason || "")}`;
    if (aiCrawlConnectEmitted.has(key)) return;
    aiCrawlConnectEmitted.add(key);
    trackAeoEvent(result === "connected" ? AEO_EVENTS.AI_CRAWL_CONNECTED : AEO_EVENTS.AI_CRAWL_FAILED, {
        ...aeoProductTelemetryBase(marketingWebsite),
        provider: "cloudflare",
        ...extra
    });
}

/** Clears the AI Crawl connect one-shot so a later reconnect in this session can emit again. */
export function resetAiCrawlConnectTelemetry(): void {
    aiCrawlConnectEmitted.clear();
}

const AEO_PROBE_SCAN_STARTED_KEY_PREFIX = "aeo_probe_scan_started:";
const trackedProbeScanRunIds = new Set<string>();

/** Emit aeo_probe_scan_started at most once per probe run_id (session + in-memory dedup). */
export function trackProbeScanStartedOnce(params: {
    marketingWebsite: string;
    runId: string;
    reason?: string;
    source?: string;
}): void {
    const runId = params.runId.trim();
    if (!runId) return;

    const dedupeKey = `${AEO_PROBE_SCAN_STARTED_KEY_PREFIX}${runId}`;
    if (trackedProbeScanRunIds.has(runId)) return;
    try {
        if (sessionStorage.getItem(dedupeKey)) return;
    } catch {
        /* sessionStorage blocked */
    }

    trackedProbeScanRunIds.add(runId);
    try {
        sessionStorage.setItem(dedupeKey, "1");
    } catch {
        /* sessionStorage blocked */
    }

    trackAeoEvent(AEO_EVENTS.PROBE_SCAN_STARTED, {
        ...aeoProductTelemetryBase(params.marketingWebsite),
        run_id: runId,
        reason: params.reason || "",
        ...(params.source ? { source: params.source } : {})
    });
}

export function persistAeoVisibilityUtmSource(): void {
    localStorage.setItem(AEO_UTM_SOURCE_KEY, AEO_VISIBILITY_UTM_VALUE);
}

export function clearAeoVisibilityUtmSource(): void {
    localStorage.removeItem(AEO_UTM_SOURCE_KEY);
}

export function consumeAeoVisibilityUtmSource(): boolean {
    const stored = localStorage.getItem(AEO_UTM_SOURCE_KEY);
    if (stored !== AEO_VISIBILITY_UTM_VALUE) return false;
    localStorage.removeItem(AEO_UTM_SOURCE_KEY);
    return true;
}

export type AeoAuditType = "citation_only" | "full";

export type AeoAuditStatus =
    | "queued"
    | "fetching"
    | "enriching"
    | "scoring"
    | "ai-visibility"
    | "synthesizing"
    | "rendering"
    | "completed"
    | "failed";

export type AeoAuditPollResponse = {
    audit_id?: string;
    type?: AeoAuditType;
    status: AeoAuditStatus;
    progress_pct: number;
    score_summary?: string | Record<string, unknown> | null;
    audit_data?: unknown;
    error?: string;
    is_report_locked?: boolean;
    report_url?: string;
};

export type StartAeoAuditInput = {
    websiteUrl: string;
    type: AeoAuditType;
    token?: string;
    email?: string;
    userId?: string;
    requestId?: string;
};

export type StartAeoAuditResult =
    | { ok: true; auditId: string; status: string }
    | { ok: false; message: string; code?: string };

/** Maps pipeline status → ScanningPanel step index (0-based). */
export const CITATION_SCAN_STATUS_TO_STEP: Record<string, number> = {
    queued: 0,
    fetching: 0,
    enriching: 3,
    scoring: 5,
    "ai-visibility": 5,
    synthesizing: 7,
    rendering: 7,
    completed: 8,
    failed: 8
};

export function getOrCreateAuditRequestId(): string {
    const existing = localStorage.getItem(AEO_AUDIT_REQUEST_ID_KEY)?.trim();
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(AEO_AUDIT_REQUEST_ID_KEY, id);
    return id;
}

export function parseScoreSummary(raw: AeoAuditPollResponse["score_summary"]): {
    overall: number;
    seoScore: number;
    schemaCoverage: number;
    aiAnswerability: string;
} {
    if (!raw) return { overall: 0, seoScore: 0, schemaCoverage: 0, aiAnswerability: "" };
    if (typeof raw === "object") {
        return {
            overall: Number(raw.overall_aeo_score) || 0,
            seoScore: Number(raw.seo_score) || 0,
            schemaCoverage: Number(raw.schema_coverage) || 0,
            aiAnswerability: String(raw.ai_answerability ?? "")
        };
    }
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
            return {
                overall: Number(parsed.overall_aeo_score) || 0,
                seoScore: Number(parsed.seo_score) || 0,
                schemaCoverage: Number(parsed.schema_coverage) || 0,
                aiAnswerability: String(parsed.ai_answerability ?? "")
            };
        }
    } catch {
        /* ignore */
    }
    return { overall: 0, seoScore: 0, schemaCoverage: 0, aiAnswerability: "" };
}

type CitationPromptRow = {
    competitors_mentioned?: string[];
    cited?: boolean;
};

type CitationProbePayload = {
    ai_visibility_score?: number;
    cited_count?: number;
    prompts?: CitationPromptRow[];
};

export type CitationPreviewData = {
    brandName: string;
    domain: string;
    overallScore: number;
    citedCount: number;
    totalPrompts: number;
    competitorBoard: Array<{ domain: string; name: string; you: boolean }>;
    rivalCount: number;
    auditDate?: string;
    showHealthMetrics: boolean;
    showLift: boolean;
    showCompetitors: boolean;
    crawlScore?: number;
    extractScore?: number;
    issuesCount?: number;
    issueCategories?: number;
};

function competitorNameToDomain(name: string): string {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed) return "";
    if (trimmed.includes("."))
        return trimmed
            .replace(/^https?:\/\//, "")
            .replace(/^www\./, "")
            .split("/")[0];
    return `${trimmed.replace(/\s+/g, "")}.com`;
}

export function buildCompetitorBoardFromEnrich(
    enrichCompetitors: CompetitorEntry[],
    brandDomain: string,
    brandName: string,
    maxRivals = 3
): { board: Array<{ domain: string; name: string; you: boolean }>; rivalCount: number } {
    const rivals = enrichCompetitors
        .filter((c) => c.domain && c.domain.toLowerCase() !== brandDomain.toLowerCase())
        .slice(0, maxRivals)
        .map((c) => ({ domain: c.domain, name: c.name || c.domain, you: false }));

    return {
        board: [...rivals, { domain: brandDomain, name: brandName, you: true }],
        rivalCount: rivals.length
    };
}

export async function fetchDomainEnrich(domain: string, token?: string): Promise<CompetitorEntry[]> {
    const parsed = await fetchDomainEnrichContext(domain, token);
    return parsed?.competitorDetails ?? [];
}

export async function fetchDomainEnrichContext(
    domain: string,
    token?: string
): Promise<ParsedEnrichedDomainContext | null> {
    if (!domain) return null;

    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;

    try {
        const res = await fetch(`${API_ENDPOINTS.AI_VISIBILITY}/domains/enrich`, {
            method: "POST",
            headers,
            body: JSON.stringify({ domain })
        });
        if (!res.ok) return null;
        const data = await res.json();
        return parseEnrichedDomainContext(data);
    } catch {
        return null;
    }
}

function competitorBoardFromCitationPrompts(
    prompts: CitationPromptRow[],
    domain: string,
    brandName: string
): { board: Array<{ domain: string; name: string; you: boolean }>; rivalCount: number } {
    const mentionCounts = new Map<string, number>();
    for (const row of prompts) {
        for (const name of row.competitors_mentioned ?? []) {
            const key = name.trim();
            if (!key) continue;
            mentionCounts.set(key, (mentionCounts.get(key) ?? 0) + 1);
        }
    }

    const rivals = [...mentionCounts.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([name]) => ({
            domain: competitorNameToDomain(name),
            name,
            you: false
        }));

    return {
        board: [...rivals, { domain, name: brandName, you: true }],
        rivalCount: rivals.length
    };
}

export function parseCitationPreviewData(
    auditData: unknown,
    scoreSummary: AeoAuditPollResponse["score_summary"],
    fallbackDomain: string,
    fallbackBrandName: string,
    enrichCompetitors: CompetitorEntry[] = []
): CitationPreviewData {
    const scores = parseScoreSummary(scoreSummary);
    const root = auditData && typeof auditData === "object" ? (auditData as Record<string, unknown>) : {};
    const probe =
        root.section_6_citation_probe && typeof root.section_6_citation_probe === "object"
            ? (root.section_6_citation_probe as CitationProbePayload)
            : null;

    const domain = String(root.domain || fallbackDomain || "").trim();
    const brandName = String(root.brand_name || fallbackBrandName || "").trim() || fallbackBrandName;
    const prompts = probe?.prompts ?? [];
    const totalPrompts = prompts.length || 5;
    const citedCount = probe?.cited_count ?? (Number.parseInt(String(scores.aiAnswerability).split("/")[0], 10) || 0);
    const overallScore =
        probe?.ai_visibility_score ??
        (typeof root.ai_visibility_score === "number" ? root.ai_visibility_score : scores.overall);

    const { board, rivalCount } =
        enrichCompetitors.length > 0
            ? buildCompetitorBoardFromEnrich(enrichCompetitors, domain, brandName)
            : competitorBoardFromCitationPrompts(prompts, domain, brandName);

    return {
        brandName,
        domain,
        overallScore: Number(overallScore) || 0,
        citedCount: Number(citedCount) || 0,
        totalPrompts,
        competitorBoard: board,
        rivalCount,
        auditDate: typeof root.audit_date === "string" ? root.audit_date : undefined,
        showHealthMetrics: true,
        showLift: true,
        showCompetitors: true,
        crawlScore: scores.seoScore,
        extractScore: scores.schemaCoverage,
        issuesCount: 0,
        issueCategories: 0
    };
}

/** Best-effort message out of a non-2xx audit response. The API answers with
 * `{error, message}` (entities.ErrorResponse); anything else falls back to the status
 * line so the caller always has something concrete to show instead of a bare failure. */
export async function readAuditResponseError(res: Response): Promise<string> {
    try {
        const data: unknown = await res.json();
        if (data && typeof data === "object") {
            const body = data as { message?: unknown; error?: unknown };
            if (typeof body.message === "string" && body.message.trim()) return body.message;
            if (typeof body.error === "string" && body.error.trim()) return body.error;
        }
    } catch {
        /* non-JSON body */
    }
    return `Request failed (${res.status}${res.statusText ? ` ${res.statusText}` : ""}).`;
}

export async function startAeoAudit(input: StartAeoAuditInput): Promise<StartAeoAuditResult> {
    const body: Record<string, string> = {
        website_url: input.websiteUrl,
        type: input.type
    };
    if (input.email) body.email = input.email;
    if (input.userId) body.user_id = input.userId;
    if (input.requestId) body.request_id = input.requestId;

    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (input.token) headers.Authorization = `Bearer ${input.token}`;

    try {
        const res = await fetch(`${API_ENDPOINTS.AI_VISIBILITY}/audits`, {
            method: "POST",
            headers,
            body: JSON.stringify(body)
        });
        let data: unknown = null;
        try {
            data = await res.json();
        } catch {
            /* non-JSON */
        }
        if (!res.ok) {
            const message =
                data &&
                typeof data === "object" &&
                "message" in data &&
                typeof (data as { message: unknown }).message === "string"
                    ? (data as { message: string }).message
                    : "Failed to start audit.";
            const code =
                data &&
                typeof data === "object" &&
                "error" in data &&
                typeof (data as { error: unknown }).error === "string"
                    ? (data as { error: string }).error
                    : undefined;
            return { ok: false, message, code };
        }
        const auditId =
            data &&
            typeof data === "object" &&
            "audit_id" in data &&
            typeof (data as { audit_id: unknown }).audit_id === "string"
                ? (data as { audit_id: string }).audit_id
                : "";
        if (!auditId) return { ok: false, message: "Audit started but no audit ID was returned." };
        const status =
            data &&
            typeof data === "object" &&
            "status" in data &&
            typeof (data as { status: unknown }).status === "string"
                ? (data as { status: string }).status
                : "queued";
        return { ok: true, auditId, status };
    } catch {
        return { ok: false, message: "Network error. Please check your connection and try again." };
    }
}

/** Reset a failed/eligible audit and re-enqueue it. Mirrors startAeoAudit's error parsing. */
export async function retryAeoAudit(auditId: string, token?: string): Promise<StartAeoAuditResult> {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    try {
        const res = await fetch(`${API_ENDPOINTS.AI_VISIBILITY}/audits/${auditId}/retry`, {
            method: "POST",
            headers
        });
        let data: unknown = null;
        try {
            data = await res.json();
        } catch {
            /* non-JSON */
        }
        if (!res.ok) {
            const message =
                data &&
                typeof data === "object" &&
                "message" in data &&
                typeof (data as { message: unknown }).message === "string"
                    ? (data as { message: string }).message
                    : "Failed to retry audit.";
            const code =
                data &&
                typeof data === "object" &&
                "error" in data &&
                typeof (data as { error: unknown }).error === "string"
                    ? (data as { error: string }).error
                    : undefined;
            return { ok: false, message, code };
        }
        const returnedId =
            data &&
            typeof data === "object" &&
            "audit_id" in data &&
            typeof (data as { audit_id: unknown }).audit_id === "string"
                ? (data as { audit_id: string }).audit_id
                : auditId;
        const status =
            data &&
            typeof data === "object" &&
            "status" in data &&
            typeof (data as { status: unknown }).status === "string"
                ? (data as { status: string }).status
                : "queued";
        return { ok: true, auditId: returnedId, status };
    } catch {
        return { ok: false, message: "Network error. Please check your connection and try again." };
    }
}

export async function fetchAeoAudit(auditId: string, token?: string): Promise<AeoAuditPollResponse | null> {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    try {
        const res = await fetch(`${API_ENDPOINTS.AI_VISIBILITY}/audits/${auditId}`, { headers });
        if (!res.ok) return null;
        return (await res.json()) as AeoAuditPollResponse;
    } catch {
        return null;
    }
}

/** Persist is_report_locked=FALSE after Starter trial checkout (idempotent). */
export async function unlockAeoReports(token?: string): Promise<boolean> {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    try {
        const res = await fetch(`${API_ENDPOINTS.AI_VISIBILITY}/audits/unlock-reports`, {
            method: "POST",
            headers
        });
        if (!res.ok) return false;
        const data = (await res.json()) as { unlocked?: boolean };
        return data.unlocked === true;
    } catch {
        return false;
    }
}

/**
 * Fire-and-forget: tell the backend a Cited By onboarding flow finished but no
 * card has been added yet (e.g. the "Looks good" step). The backend arms the
 * recurring no-card nurture drip; it is cancelled automatically once a card /
 * trial is added. Idempotent server-side, so calling more than once is safe.
 * All fields are hints — the backend fills gaps from the caller's latest audit.
 */
export async function notifyOnboardingCompleted(input: {
    domain?: string;
    email?: string;
    token?: string;
}): Promise<void> {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (input.token) headers.Authorization = `Bearer ${input.token}`;

    try {
        await fetch(`${API_ENDPOINTS.AI_VISIBILITY}/onboarding/completed`, {
            method: "POST",
            headers,
            body: JSON.stringify({
                ...(input.domain ? { domain: input.domain } : {}),
                ...(input.email ? { email: input.email } : {})
            })
        });
    } catch {
        /* best-effort — must never block onboarding */
    }
}

export function isAuditTerminal(status: string): boolean {
    return status === "completed" || status === "failed";
}
